import type { AxiosHeaders } from "axios"
import type { TCompanyActivity, TDBItem } from "@/types"
import { api } from "./api"
import type { IAPIParams, TAPIPaginateHeaders } from "./types"

export function generateSetupActivity<T = TCompanyActivity>(url: string) {
  return {
    key: url,
    fetch: () => api.get(url).then((res) => res.data as T),
    update: (payload: Partial<T>) => api.put<T>(url, payload).then((res) => res.data as T),
  }
}

interface IParams {
  create?: boolean
  update?: boolean
  remove?: boolean
}

export interface IAPIResource<T extends TDBItem> {
  url: (opts?: any) => string
  fetchMany: (
    params?: IAPIParams & { seed?: string },
    opts?,
  ) => Promise<{ data: T[]; headers: AxiosHeaders & TAPIPaginateHeaders }>
  fetch: (id: string, opts?) => Promise<T>
  create?: (resource: Partial<T> | Partial<T>[], opts?) => Promise<T>
  update?: (resource: Partial<T> & TDBItem, opts?) => Promise<T>
  remove?: (id: string, opts?) => Promise<string>
}

export function generateAPIResource<T extends TDBItem>(
  rawUrl: string | ((opts) => string),
  { create, update, remove }: IParams = { create: true, update: true, remove: true },
): IAPIResource<T> {
  const urlBuilder = typeof rawUrl === "function" ? rawUrl : () => rawUrl

  const resource: IAPIResource<T> = {
    url: urlBuilder,

    // Fetch all resources with optional parameters
    fetchMany: async (params?: IAPIParams, opts?) => {
      const url = urlBuilder(opts)
      const { data, headers } = await api.get<T[]>(url, { params })
      return {
        data,
        headers: headers as AxiosHeaders & TAPIPaginateHeaders,
      }
    },

    // Fetch a single resource by ID
    fetch: async (id: string, opts) => {
      const url = urlBuilder(opts)
      const res = await api.get(`${url}/${id}`)
      return res.data as T
    },
  }

  if (create)
    resource.create = async (resource: Partial<T> | Partial<T>[], opts) => {
      const url = urlBuilder(opts)
      const res = await api.post<T>(url, resource)
      return res.data
    }

  if (update)
    resource.update = async (resource: Partial<T> & TDBItem, opts) => {
      const url = urlBuilder(opts)
      const res = await api.put<T>(`${url}/${resource.id}`, resource)
      return res.data
    }

  if (remove)
    resource.remove = async (id: string, opts) => {
      const url = urlBuilder(opts)
      const res = await api.delete<string>(`${url}/${id}`)
      return res.data
    }

  return resource
}
